import { useMemo } from 'react';

import type { AgentKnowledgeConfiguration } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import {
	useConnectorsIsFetching,
	useEnabledKnowledgeSources,
} from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { useEnsureConnectorsLoaded } from '@atlassian/conversation-assistant-store/controllers/connectors-store/use-ensure-connectors-loaded';

import { useProductSortOrder } from '../../../../common/ui/useProductSortOrder';

import { getKnowledgeSourceDisplayName } from './utils/get-knowledge-source-display-name';

function mapSourceToOption(source: AgentKnowledgeConfiguration): {
	label: string;
	value: string;
	iconUrl?: string;
} {
	return {
		label: getKnowledgeSourceDisplayName(source.source, source.friendlyName),
		value: source.source,
		iconUrl: source.iconUrl,
	};
}

export type AppOption = { label: string; value: string; iconUrl?: string };

export function useAppOptionsForSelect(
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'],
): { options: AppOption[]; isLoading: boolean } {
	useEnsureConnectorsLoaded({ assistanceServiceParams });
	const [storeAllSources] = useEnabledKnowledgeSources();
	const [storeIsFetching] = useConnectorsIsFetching();

	const productSortOrderProvider = useProductSortOrder();

	const sources = useMemo(() => {
		return storeAllSources.filter((source) => source.isUserOAuthed === true);
	}, [storeAllSources]);

	const sortQueryKey = useMemo(
		() => ['app-options-sorted', sources.map((s) => s.source).join(',')],
		[sources],
	);

	const { data: sortedOptions, isFetching: isSorting } = useQuery({
		queryKey: sortQueryKey,
		queryFn: async () => {
			const sortFn = await productSortOrderProvider();
			return sources.map(mapSourceToOption).sort((a, b) => sortFn(a.value, b.value));
		},
		enabled: sources.length > 0,
	});

	const options = sortedOptions ?? [];

	return { options, isLoading: storeIsFetching || isSorting };
}
